// Personalisation line split/merge shared by both builders, matching the cart
// drawer (assets/personalisation-drawer.js, assets/cart.js).

export type PersonalisationData = {
  initials: string;
  font: string;
  color: string;
};

export type PersonalisableLine = {
  key: string;
  variantId: number;
  quantity: number;
  personalisation?: PersonalisationData | null;
  monogramVariantId?: number | null;
};

/**
 * Apply personalisation to line `key`. Updates in place when the line is already
 * personalised or has one unit; otherwise splits one unit into a new line built
 * from `freshLine` (which supplies a fresh key/group identity).
 */
export function addPersonalisation<T extends PersonalisableLine>(
  lines: T[],
  key: string,
  personalisation: PersonalisationData,
  monogramVariantId: number | null,
  freshLine: (base: T) => T,
): T[] {
  const idx = lines.findIndex(l => l.key === key);
  if (idx === -1) return lines;
  const line = lines[idx];

  if (line.personalisation || line.quantity <= 1) {
    const next = [...lines];
    next[idx] = {...line, personalisation, monogramVariantId: monogramVariantId ?? line.monogramVariantId ?? null};
    return next;
  }

  const next = [...lines];
  next[idx] = {...line, quantity: line.quantity - 1};
  const split: T = {
    ...freshLine(line),
    quantity: 1,
    personalisation,
    monogramVariantId: monogramVariantId ?? line.monogramVariantId ?? null,
  };
  next.splice(idx + 1, 0, split);
  return next;
}

/**
 * Remove personalisation from line `key`, merging it back into a matching plain
 * line of the same variant (combining quantities) when one exists. `matches`
 * adds equality beyond the variant id (e.g. same gift box / note).
 */
export function removePersonalisation<T extends PersonalisableLine>(
  lines: T[],
  key: string,
  matches: (target: T, line: T) => boolean,
): T[] {
  const idx = lines.findIndex(l => l.key === key);
  if (idx === -1) return lines;
  const line = lines[idx];

  const targetIdx = lines.findIndex(
    (l, i) => i !== idx && !l.personalisation && l.variantId === line.variantId && matches(l, line),
  );
  if (targetIdx === -1) {
    const next = [...lines];
    next[idx] = {...line, personalisation: null};
    return next;
  }

  const next = [...lines];
  next[targetIdx] = {...next[targetIdx], quantity: next[targetIdx].quantity + line.quantity};
  next.splice(idx, 1);
  return next;
}
